import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '@/lib/api'
import { formatLbp, formatMoment, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Modal, Toggle } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import type { Customer } from '@/pages/customers/CustomersPage'
import { ReverseButton, ReverseDialog, ReversedBadge } from '@/components/ReverseDialog'
import { t } from '@/i18n'
import { ListFilters, Pager, NO_FILTERS, type ListFilterState } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'

interface LedgerRow {
  id: number
  type: string
  amountUsd: number
  amountLbp: number
  balanceAfterUsd: number
  occurredAtUtc: string
  dueDate: string | null
  note: string | null
  isReversed: boolean
  reversalReason: string | null
  isReversal: boolean
}

export function CustomerStatement({
  customer,
  onClose,
}: {
  customer: Customer | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const canManage = useSession((state) => state.can)(Permission.ManageCustomers)

  const [usd, setUsd] = useState('')
  const [lbp, setLbp] = useState('')
  const [isCash, setIsCash] = useState(true)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  /** The payment being undone. */
  const [reversing, setReversing] = useState<LedgerRow | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => apiClient.get<{ id: number }[]>('/api/registers'),
    enabled: customer !== null,
  })
  const registerId = registers.data?.[0]?.id

  const shift = useQuery({
    queryKey: ['shift', registerId],
    queryFn: () => apiClient.get<{ id: number } | null>(`/api/shifts/current?registerId=${registerId}`),
    enabled: registerId != null,
  })

  const [filters, setFilters] = useState<ListFilterState>(NO_FILTERS)

  const statement = usePagedList<LedgerRow>(
    ['statement', customer?.id],
    `/api/customers/${customer?.id ?? 0}/statement`,
    { from: filters.from, to: filters.to },
    { enabled: customer !== null },
  )

  const pay = useMutation({
    mutationFn: () =>
      apiClient.post('/api/customers/payments', {
        customerId: customer!.id,
        amountUsd: Number(usd || 0),
        amountLbp: Number(lbp || 0),
        shiftId: shift.data?.id ?? null,
        isCash,
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      setUsd('')
      setLbp('')
      setNote('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['statement'] })
      await queryClient.invalidateQueries({ queryKey: ['customers'] })
      await queryClient.invalidateQueries({ queryKey: ['receivables'] })
      await queryClient.invalidateQueries({ queryKey: ['shift-expected'] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not record the payment.',
      ),
  })

  // Cash has to land in an open till, so the button says why it cannot be taken.
  const noTillOpen = isCash && !shift.data

  return (
    <Modal
      open={customer !== null}
      onClose={onClose}
      wide
      title={customer?.name ?? ''}
      description={
        customer
          ? `Owes ${formatUsd(customer.balanceUsd)} of a ${formatUsd(customer.creditLimitUsd)} limit`
          : undefined
      }
      footer={<Button variant="secondary" onClick={onClose}>{t('Close')}</Button>}
    >
      {canManage && (
        <section className="surface-sunken mb-5 rounded-xl p-4">
          <p className="mb-3 text-sm font-medium">{t('Take a payment')}</p>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t('US dollars')}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={usd}
              onChange={(e) => setUsd(e.target.value)}
              className="tabular"
              placeholder="0.00"
            />
            <Input
              label={t('Lebanese lira')}
              type="number"
              inputMode="numeric"
              min="0"
              value={lbp}
              onChange={(e) => setLbp(e.target.value)}
              className="tabular"
              placeholder="0"
            />
          </div>

          <Input
            label={t('Note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            wrapperClassName="mt-3"
            placeholder={t('Optional')}
          />

          <div className="mt-3">
            <Toggle
              checked={isCash}
              onChange={setIsCash}
              label={t('Paid in cash')}
              hint={
                isCash
                  ? 'Goes into the open till, so the drawer reconciles at close'
                  : 'A transfer or card — nothing enters the till'
              }
            />
          </div>

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}

          <Button
            className="mt-3"
            full
            onClick={() => pay.mutate()}
            disabled={pay.isPending || noTillOpen || (Number(usd || 0) <= 0 && Number(lbp || 0) <= 0)}
          >
            {pay.isPending ? 'Recording…' : noTillOpen ? 'Open a till to take cash' : 'Record payment'}
          </Button>
        </section>
      )}

      <p className="mb-2 text-sm font-medium">{t('Statement')}</p>
      {/* No search: a ledger row is a charge or a payment, not something with a number to quote. */}
      <ListFilters value={filters} onChange={setFilters} searchHint="" showSearch={false} />

      {statement.page.length === 0 ? (
        <p className="text-sm text-[var(--text-secondary)]">{t('Nothing on this account yet.')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {statement.page.map((row) => (
            <li
              key={row.id}
              className="surface-raised flex items-center gap-3 rounded-lg border p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {/* A charge increases the debt; a payment or credit reduces it. */}
                  <Badge tone={row.amountUsd > 0 ? 'warn' : 'success'}>{row.type}</Badge>
                  <span className="truncate text-xs text-[var(--text-secondary)]">
                    {formatMoment(row.occurredAtUtc)}
                  </span>
                  {row.isReversed && <ReversedBadge reason={row.reversalReason} />}
                </div>
                {row.note && (
                  <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">{row.note}</p>
                )}
              </div>

              <div className="shrink-0 text-right">
                <p
                  className={`tabular text-sm font-medium ${
                    row.isReversed
                      ? 'text-[var(--text-secondary)] line-through'
                      : row.amountUsd > 0
                        ? 'text-warn-500'
                        : 'text-success-500'
                  }`}
                >
                  {row.amountUsd > 0 ? '+' : ''}
                  {formatUsd(row.amountUsd)}
                </p>
                {row.amountLbp > 0 && (
                  <p className="tabular text-xs text-[var(--text-secondary)]">
                    {formatLbp(row.amountLbp)}
                  </p>
                )}
                <p className="tabular text-xs text-[var(--text-secondary)]">
                  bal {formatUsd(row.balanceAfterUsd)}
                </p>
              </div>

              {/* Only a payment is undone from here. A charge belongs to a sale and a credit to
                  a refund, each of which has its own remedy — the server refuses either. */}
              {row.type === 'Payment' && !row.isReversal && (
                <ReverseButton
                  isReversed={row.isReversed}
                  label={`Reverse this payment`}
                  onClick={() => setReversing(row)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <Pager list={statement} />

      <ReverseDialog
        kind="accountPayment"
        id={reversing?.id ?? null}
        what={
          reversing
            ? `${formatUsd(Math.abs(reversing.amountUsd))} received on ${new Date(reversing.occurredAtUtc).toLocaleDateString()}`
            : ''
        }
        onClose={() => setReversing(null)}
        invalidate={['statement', 'customers', 'receivables', 'shift-expected']}
      />
    </Modal>
  )
}
